import { cva } from "class-variance-authority";
import { uniqBy } from "lodash";
import { ReactNode, useEffect, useRef, useState } from "react";
import { TooltipItem } from "src/utils/Tooltip";
import { twMerge } from "tailwind-merge";
import { UserAvatar2, UserAvatarStore } from "./avatar/Avatar";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { selectUsers } from "src/modules/users/selector";
import useAsyncEffect from "src/hooks/useAsyncEffect";
import { nameFromEmail, shortNameFromEmail } from "src/utils/users.utils";
import { addUsers } from "src/modules/users/actions";

import { addParamsAndRequest } from "src/constants";
import _ from "lodash";

export type StatusType = "pending" | "success" | "denied" | "canceled";
export type UserAvatarType = {
  name: string;
  avatar: string | null;
  id?: number;
  status?: StatusType;
  listId?: number;
  email?: string;
  shortName: string;
};

const TGUserAvatarsStyle = cva("inline-flex w-full h-full rounded-full object-cover justify-center items-center", {
  variants: {
    status: {
      pending: "ring-white ring-1",
      success: "ring-[#22AD5C] ring-2",
      canceled: "ring-[#18BFFF] ring-2",
      denied: "ring-[#BA0C2F] ring-2",
    },
    picture: {
      false: "bg-darkblue text-white",
    },
  },
  defaultVariants: {
    status: "pending",
    picture: false,
  },
});

function TGUserAvatarsList({
  items,
  classWrapper,
  classImg,
  last,
}: {
  items: UserAvatarType[];
  classWrapper?: string;
  classImg?: string;
  last?: ReactNode;
}) {
  return (
    <div className={twMerge("flex -space-x-1", classWrapper)}>
      {items.map((item: UserAvatarType, i: number) => (
        <TooltipItem
          wrapperClassName="flex shrink-0"
          className="-translate-x-1 shrink-0"
          position="top-left"
          tooltipsText={item.name}
          key={i}
        >
          {!!item.avatar ? (
            <img
              className={twMerge(TGUserAvatarsStyle({ status: item.status }), classImg)}
              src={item.avatar}
              alt={item.name}
            />
          ) : (
            <span className={twMerge(TGUserAvatarsStyle({ status: item.status, picture: !!item.avatar }), classImg)}>
              {item.shortName}
            </span>
          )}
        </TooltipItem>
      ))}
      {last && last}
    </div>
  );
}

export type UserAvatarType2 = {
  name: string;
  id?: number;
  status?: StatusType;
  listId?: number;
  email?: string;
  shortName: string;
};

export function UserAvatars({ users }: { users: UserAvatarType[] }) {
  const [avatar, setAvatar] = useState<ReactNode | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const renderAvatars = (containerWidth: number, employees: UserAvatarType[]) => {
    const countItems = Math.ceil(containerWidth / 26);
    const remainingCount = employees.length - countItems + 1;

    const slidedItems = employees.slice(0, countItems - 1);

    return (
      <TGUserAvatarsList2
        items={slidedItems}
        classImg="w-7.5 h-7.5"
        last={
          countItems >= employees.length ? null : (
            <div
              className={twMerge(
                employees.length > 100 ? "bg-[#BA0C2F]" : "bg-gray-500",
                "flex -translate-x-1 shrink-0 w-7.5 h-7.5 rounded-full ring-white ring-1 items-center justify-center text-white text-[10px]"
              )}
            >
              +{remainingCount >= 999 ? 999 : remainingCount}
            </div>
          )
        }
      />
    );
  };

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const node = renderAvatars(containerWidth, users);
        setAvatar(node);
      }
    };

    if (containerRef.current) {
      handleResize();
    }
  }, [JSON.stringify(users)]);

  return (
    <div id="avatar-container" ref={containerRef} className="w-full">
      {users.length > 0 && avatar}
    </div>
  );
}

export function TGUserAvatarsList2({
  items,
  classWrapper,
  classImg,
  last,
}: {
  items: UserAvatarType2[];
  classWrapper?: string;
  classImg?: string;
  last?: ReactNode;
}) {
  return (
    <div className={twMerge("flex -space-x-1", classWrapper)}>
      {uniqBy(items, "email").map((item: UserAvatarType2) => (
        <UserAvatarItem
          classImg={twMerge(TGUserAvatarsStyle({ status: item.status }), classImg)}
          item={item}
          key={item.email}
        />
      ))}
      {last && last}
    </div>
  );
}

export default TGUserAvatarsList;

export function UserAvatarItem({
  item,
  classImg,
  single = false,
  classNameText,
}: {
  item: UserAvatarType2;
  classImg?: string;
  single?: boolean;
  classNameText?: string;
}) {
  const users: any = useAppSelector(selectUsers);
  const dispatch = useAppDispatch();

  const [user, setUser] = useState({
    name: item.name || nameFromEmail(item.email!) + " (Нет номера)",
    shortName: item.shortName || shortNameFromEmail(item.email!),
  });

  useEffect(() => {
    addParamsAndRequest(
      item.email || "33804303@partnerabie.com",
      async (allParams: any) => {
        dispatch(addUsers([..._.uniqWith(allParams, _.isEqual)]));
      },
      100 // задержка перед запросом (мс)
    );
  }, [item.email]);

  useEffect(() => {
    if (item.email) {
      setUser({
        name:
          (users[item.email.toLowerCase().trim()] && users[item.email.toLowerCase().trim()]["displayName"]) ||
          nameFromEmail(item.email!) + " (Нет номера)",
        shortName:
          (users[item.email.toLowerCase().trim()] && users[item.email.toLowerCase().trim()]["shortName"]) ||
          shortNameFromEmail(item.email!),
      });
    }
  }, [item.email, users]);

  return (
    <TooltipItem
      wrapperClassName="flex shrink-0"
      className="-translate-x-1 shrink-0"
      position="top-left"
      tooltipsText={user.name}
      disable={single}
    >
      <UserAvatar2
        displayName={single ? user.name : item.name}
        email={item.email}
        shortName={user.shortName}
        className={classImg}
        classNameText={classNameText}
        single={single}
      />
    </TooltipItem>
  );
}

export function UserAvatarItemStore({
  item,
  classImg,
  single = false,
  classNameText,
}: {
  item: UserAvatarType;
  classImg?: string;
  single?: boolean;
  classNameText?: string;
}) {
  const users = useAppSelector((state) => selectUsers(state));
  const usersStatus = useAppSelector((state) => state.users.status);

  const [user, setUser] = useState<UserAvatarType>({
    name: item.name || nameFromEmail(item.email!) + " (Нет номера)",
    shortName: item.shortName || shortNameFromEmail(item.email!),
    avatar: null,
  });

  useAsyncEffect(async () => {
    if (item.email && usersStatus === "IDLE" && users[item.email]) {
      const userState = users[item.email];

      if (userState && userState.displayName) {
        setUser({
          name: userState.displayName,
          shortName: userState.shortName || "",
          avatar: userState.avatar || null,
        });
      }
    }
  }, [item.email, usersStatus]);

  return (
    <TooltipItem
      wrapperClassName="flex shrink-0"
      className="-translate-x-1 shrink-0"
      position="top-left"
      tooltipsText={user.name}
      disable={single}
    >
      <UserAvatarStore
        displayName={user.name}
        email={item.email}
        image={user.avatar}
        shortName={user.shortName}
        className={classImg}
        classNameText={classNameText}
        single={single}
      />
    </TooltipItem>
  );
}
